import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Icon from "../../../components/AppIcon";
import { cn } from "../../../utils/cn";

function useScrollOverflowEdge(scrollRef) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const max = scrollWidth - clientWidth;
    const epsilon = 2;
    setCanScrollLeft(scrollLeft > epsilon);
    setCanScrollRight(max > epsilon && scrollLeft < max - epsilon);
  }, [scrollRef]);

  useLayoutEffect(() => {
    update();
  }, [update]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [scrollRef, update]);

  return { canScrollLeft, canScrollRight, update };
}

/**
 * Horizontal stepper for the contract generator. All steps are always clickable so
 * users can jump to any section without advancing sequentially.
 * Scrolls horizontally with arrows and a thin scrollbar when content overflows.
 */
const ContractFormStepper = ({
  steps,
  activeIndex,
  onStepChange,
  className,
}) => {
  const scrollRef = useRef(null);
  const { canScrollLeft, canScrollRight, update } = useScrollOverflowEdge(scrollRef);

  const scrollByDirection = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = Math.max(120, Math.floor(el.clientWidth * 0.45));
    el.scrollBy({ left: dir * delta, behavior: "smooth" });
    window.requestAnimationFrame(() => update());
  };

  useLayoutEffect(() => {
    update();
  }, [steps, update]);

  useEffect(() => {
    const el = scrollRef.current?.querySelector(
      `[data-step-index="${activeIndex}"]`
    );
    el?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [activeIndex]);

  if (!Array.isArray(steps) || steps.length === 0) return null;

  return (
    <nav
      aria-label="Etapas do formulário"
      className={cn("w-full", className)}
    >
      <div className="flex items-stretch gap-1 w-full min-w-0">
        <button
          type="button"
          className={cn(
            "shrink-0 self-center rounded-lg border border-border bg-card p-2 shadow-soft-sm transition-smooth",
            canScrollLeft
              ? "text-foreground hover:bg-muted"
              : "cursor-default text-muted-foreground/50"
          )}
          disabled={!canScrollLeft}
          onClick={() => scrollByDirection(-1)}
          aria-label="Rolar etapas para a esquerda"
        >
          <Icon name="ChevronLeft" size={20} />
        </button>

        <div
          ref={scrollRef}
          className={cn(
            "flex-1 min-w-0 overflow-x-auto overflow-y-hidden scroll-smooth rounded-xl border border-border/60 bg-muted/30 px-1 py-1",
            "[scrollbar-width:thin]",
            "[scrollbar-color:hsl(var(--border))_transparent]",
            "[&::-webkit-scrollbar]:h-2",
            "[&::-webkit-scrollbar-track]:rounded-full",
            "[&::-webkit-scrollbar-track]:bg-transparent",
            "[&::-webkit-scrollbar-thumb]:rounded-full",
            "[&::-webkit-scrollbar-thumb]:bg-border hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/40"
          )}
        >
          <div className="flex items-center gap-1 sm:gap-2 w-max min-h-[4.5rem] pr-1">
            {steps.map((step, index) => {
              const isActive = index === activeIndex;
              const isComplete = index < activeIndex;

              return (
                <React.Fragment key={step.id}>
                  {index > 0 && (
                    <div
                      className={cn(
                        "h-0.5 w-4 sm:w-6 sm:min-w-[12px] shrink-0 rounded-full transition-colors",
                        index <= activeIndex ? "bg-primary/60" : "bg-border"
                      )}
                      aria-hidden
                    />
                  )}
                  <button
                    type="button"
                    data-step-index={index}
                    onClick={() => onStepChange(index)}
                    className={cn(
                      "flex flex-col items-center gap-1.5 min-w-[5.5rem] max-w-[9rem] px-1 py-1 rounded-lg transition-colors shrink-0",
                      "cursor-pointer hover:bg-muted/70"
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold border-2 transition-colors",
                        isActive &&
                          "border-primary bg-primary text-primary-foreground",
                        isComplete &&
                          !isActive &&
                          "border-primary/80 bg-primary/10 text-primary",
                        !isActive &&
                          !isComplete &&
                          "border-border bg-muted/40 text-muted-foreground"
                      )}
                    >
                      {isComplete ? "✓" : index + 1}
                    </span>
                    <span
                      className={cn(
                        "text-[11px] sm:text-xs text-center leading-tight line-clamp-2",
                        isActive && "font-semibold text-foreground",
                        !isActive && "text-muted-foreground"
                      )}
                    >
                      {step.label}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className={cn(
            "shrink-0 self-center rounded-lg border border-border bg-card p-2 shadow-soft-sm transition-smooth",
            canScrollRight
              ? "text-foreground hover:bg-muted"
              : "cursor-default text-muted-foreground/50"
          )}
          disabled={!canScrollRight}
          onClick={() => scrollByDirection(1)}
          aria-label="Rolar etapas para a direita"
        >
          <Icon name="ChevronRight" size={20} />
        </button>
      </div>
    </nav>
  );
};

export default ContractFormStepper;
